import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { Button } from "@progress/kendo-react-buttons";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { formatIndianNumber } from "../../_helper/helper";
import { clearExpenseApprovedDetails } from "./expenseApprovedSlice";
import { verifySRExpense } from "./services/expenseApproved.services";
import FormTextArea from "../../components/formFields/FormTextArea";
import { getSRExpenseByID } from "../expensedetails/services/expenseDetails.services";
import moment from "moment";
import { clearExpenseDetail } from "../expensedetails/expenseDetailsSlice";
import { getAllExpenseSummaryReport } from "../reports/services/reports.services";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const ExpenseChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const ExpenseDetail = useAppSelector(
    (state) => state.expensedetails.ExpenseDetail
  );
  const net_amount = +formRenderProps.valueGetter("net_amount") || 0;
  const disallowed_amount =
    +formRenderProps.valueGetter("disallowed_amount") || 0;
  const additional_expense_amount =
    +formRenderProps.valueGetter("additional_expense_amount") || 0;
  useEffect(() => {
    formRenderProps.onChange("net_amount", {
      value: ExpenseDetail?.net_amount,
    });
    formRenderProps.onChange("disallowed_amount", {
      value: ExpenseDetail?.disallowed_amount,
    });
    formRenderProps.onChange("additional_expense_amount", {
      value: ExpenseDetail?.additional_expense_amount,
    });
    formRenderProps.onChange("verification_remarks", {
      value: ExpenseDetail?.verification_remarks,
    });
  }, [ExpenseDetail]);

  useEffect(() => {
    formRenderProps.onChange("total_amount", {
      value: net_amount + additional_expense_amount - disallowed_amount,
    });
  }, [net_amount, additional_expense_amount, disallowed_amount]);
  return null;
};

const CreateExpenseApproved = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const gridRef = useRef<any>(null);
  const expense_details_guid = location.state?.expense_details_guid;
  const isreport = location.state?.isreport;
  const loading = useAppSelector((state) => state.expenseApproved.loading);
  const formLoading = useAppSelector(
    (state) => state.expenseApproved.formLoading
  );
  const ExpenseApprovedDetails = useAppSelector(
    (state) => state.expenseApproved.ExpenseApprovedDetails
  );
  const ExpenseDetail = useAppSelector(
    (state) => state.expensedetails.ExpenseDetail
  );
  const ExpenseSummaryReportDetails = useAppSelector(
    (state) => state.reports.ExpenseSummaryReportDetails
  );
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ExpenseApprovedDetails]);

  useEffect(() => {
    if (expense_details_guid) {
      dispatch(getSRExpenseByID(expense_details_guid));
    }
  }, [expense_details_guid]);

  useEffect(() => {
    return () => {
      dispatch(clearExpenseApprovedDetails());
      dispatch(clearExpenseDetail());
    };
  }, []);

  //   const breakPoint = useResponsiveJSX([530, 930]);
  //   const dynamicColSpan = [4, 2, 0][breakPoint] ?? 0;

  const handleSubmit = async (values: any) => {
    const formdata = {
      total_amount: values?.total_amount,
      net_amount: values?.net_amount,
      disallowed_amount: values?.disallowed_amount,
      additional_expense_amount: values?.additional_expense_amount,
      verification_remarks: values?.verification_remarks,
      verify_by: localStorage.getItem("UserID"),
      verification_date: moment(new Date()).format("YYYY-MM-DD"),
    };
    if (expense_details_guid) {
      try {
        const updatePayload: any = {
          // id: values?.id ? +values?.id : null,
          expense_details_guid: expense_details_guid,
          ...formdata,
        };

        const response = await dispatch(verifySRExpense(updatePayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (isreport) {
            const reportpayload = {
              sr_id: ExpenseSummaryReportDetails?.sr_id,
              from_date: ExpenseSummaryReportDetails?.from_date
                ? moment(ExpenseSummaryReportDetails?.from_date).format(
                    "YYYY-MM-DD"
                  )
                : "",
              to_date: ExpenseSummaryReportDetails?.to_date
                ? moment(ExpenseSummaryReportDetails?.to_date).format(
                    "YYYY-MM-DD"
                  )
                : "",
            };
            await dispatch(getAllExpenseSummaryReport(reportpayload));
            navigate(-1);
          } else {
            navigate("/srexpenses");
          }
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  const handleFormClose = () => {
    if (isreport) {
      navigate(-1);
    } else {
      navigate("/srexpenses");
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={ExpenseApprovedDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <Typography.h5>Expense Details</Typography.h5>
              <table
                className="table table-bordered"
                style={{ borderColor: "black" }}
              >
                <tbody>
                  <tr>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Expense Date</strong>
                    </td>
                    <td>
                      {moment(ExpenseDetail?.expense_date).format("DD/MM/YYYY")}{" "}
                    </td>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Sales Representative</strong>
                    </td>
                    <td>{ExpenseDetail?.sr_name}</td>
                  </tr>
                  <tr>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>To City</strong>
                    </td>
                    <td>{ExpenseDetail?.to_city_name}</td>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>From City</strong>
                    </td>
                    <td>{ExpenseDetail?.from_city_name}</td>
                  </tr>
                  <tr>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Mode Of Transport</strong>
                    </td>
                    <td>{ExpenseDetail?.mode_of_transport_name}</td>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Petrol Allowance</strong>
                    </td>
                    <td className="text-end">
                      {formatIndianNumber(
                        ExpenseDetail?.petrol_rate_per_km || 0
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td rowSpan={2} style={{ backgroundColor: "lightgrey" }}>
                      <strong>Cities Visited</strong>
                    </td>
                    <td rowSpan={2}>
                      {ExpenseDetail?.city_town_visisted_name}
                    </td>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Fare For Transport</strong>
                    </td>
                    <td className="text-end">
                      {formatIndianNumber(
                        ExpenseDetail?.fare_for_transport || 0
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Sales Amount</strong>
                    </td>
                    <td className="text-end">
                      {formatIndianNumber(ExpenseDetail?.sales_amount || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ backgroundColor: "lightgrey" }}>
                      <strong>Working agent/Area/Town:</strong>
                    </td>
                    <td colSpan={3}>{ExpenseDetail?.remarks}</td>
                  </tr>
                </tbody>
              </table>
              <Typography.h5>Expenses</Typography.h5>
              <table
                className="table table-bordered"
                style={{ borderColor: "black" }}
              >
                <tbody>
                  {ExpenseDetail.expense_details_list.map((item: any) => (
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>{item?.expense_name?.toUpperCase()}</strong>
                      </td>
                      <td style={{ textAlign: "end" }}>
                        {formatIndianNumber(item.value || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, marginTop: 10 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[{ width: "50%" }, { width: "50%" }]}
              >
                <GridLayoutItem colSpan={2}>
                  <Typography.h4>Expense Verification</Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <hr />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px 10px",
                  }}
                >
                  <Typography.h6>Net Amount</Typography.h6>
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    textAlign: "end",
                    marginRight: "13px",
                  }}
                >
                  <Typography.h6>
                    {formatIndianNumber(
                      formRenderProps.valueGetter("net_amount")
                    )}
                  </Typography.h6>
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px 10px",
                  }}
                >
                  <Typography.h6>Disallowed Amount</Typography.h6>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ margin: 0, padding: "5px" }}
                    wrapperClassName="right-alighned-field"
                    name="disallowed_amount"
                    max={formRenderProps.valueGetter("net_amount")}
                    placeholder="0"
                    component={FormNumericTextField}
                    // type="number"
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px 10px",
                  }}
                >
                  <Typography.h6>Additional Expense Amount</Typography.h6>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ margin: 0, padding: "5px" }}
                    wrapperClassName="right-alighned-field"
                    name="additional_expense_amount"
                    placeholder="0"
                    component={FormNumericTextField}

                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px 10px",
                  }}
                >
                  <Typography.h6>Total Amount</Typography.h6>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    textAlign: "end",
                    marginRight: "13px",
                  }}
                >
                  <Typography.h6>
                    {formatIndianNumber(
                      formRenderProps.valueGetter("total_amount") || 0
                    )}
                  </Typography.h6>
                </GridLayoutItem>
                <GridLayoutItem style={{ padding: "0 10px" }}>
                  <Field
                    wrapperClassName="m-0"
                    name="verification_remarks"
                    label="Verification Remarks"
                    placeholder="type..."
                    component={FormTextArea}
                    rows={5}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={2}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={expense_details_guid ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || formLoading}
                    loading={formLoading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 4 }}
                    onClick={handleFormClose}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>

            <ExpenseChangeWatcher formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateExpenseApproved;
